/*
 * Who may sign in.
 *
 * This list is the whole of the access control. There is no self-registration
 * and no invitation link: a number is on this list or it cannot get in, and
 * removing it ends any live session immediately rather than at the next
 * sign-in.
 *
 * A person here can only ever see and trade their own Zerodha account. An
 * admin can add and remove people; that is all the admin flag does. It does
 * not grant sight of anybody else's strategies or positions — which is why
 * this page shows no P&L for anybody.
 */

import { useState } from "react";

import { useAddPerson, useEditPerson, useMe, usePeople, useRemovePerson } from "../api/hooks";
import type { Person } from "../api/types";
import { IconPlus, IconTrash } from "../components/Icons";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { clockTime } from "../lib/format";

export function PeoplePage() {
  const me = useMe();
  const people = usePeople(true);
  const [adding, setAdding] = useState(false);

  const rows = people.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>People</h1>
          <p>
            Only these mobile numbers can sign in. Everyone sees and trades their own
            Zerodha account and nobody else's — including admins.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={() => setAdding(true)}>
            <IconPlus size={15} /> Add a number
          </button>
        </div>
      </div>

      <div className="card">
        {people.isPending ? (
          <div className="empty"><span className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div className="empty"><strong>Nobody yet</strong></div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <PersonRow key={p.id} person={p} isMe={p.id === me.data?.user.id} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <AddDialog onClose={() => setAdding(false)} />}
    </>
  );
}

function PersonRow({ person, isMe }: { person: Person; isMe: boolean }) {
  const edit = useEditPerson();
  const remove = useRemovePerson();
  const toast = useToast();

  const patch = (body: Record<string, unknown>) =>
    edit.mutate({ id: person.id, body }, { onError: (e) => toast.fail(e) });

  return (
    <tr>
      <td>
        <div className="sym">
          {person.name} {isMe && <span className="pill">you</span>}
        </div>
      </td>
      <td className="num">{person.mobile}</td>
      <td>
        <button
          className={`pill ${person.isAdmin ? "on" : "off"}`}
          style={{ cursor: isMe ? "default" : "pointer" }}
          disabled={isMe || edit.isPending}
          onClick={() => patch({ isAdmin: !person.isAdmin })}
          title={isMe ? "You cannot change your own role." : "Change role"}
        >
          {person.isAdmin ? "Admin" : "Member"}
        </button>
      </td>
      <td>
        <button
          className={`pill ${person.isActive ? "live" : "danger"}`}
          style={{ cursor: isMe ? "default" : "pointer" }}
          disabled={isMe || edit.isPending}
          onClick={() => patch({ isActive: !person.isActive })}
          title={isMe ? "You cannot deactivate yourself." : "Change status"}
        >
          {person.isActive ? "Active" : "Blocked"}
        </button>
      </td>
      <td className="small faint">
        {person.lastLoginAt ? clockTime(person.lastLoginAt) : "never"}
      </td>
      <td>
        {!isMe && (
          <button
            className="icon-btn"
            aria-label={`Remove ${person.name}`}
            disabled={remove.isPending}
            onClick={() => {
              if (
                !window.confirm(
                  `Remove ${person.name}? Their strategies, orders and positions go with them.`,
                )
              )
                return;
              remove.mutate(person.id, {
                onSuccess: () => toast.ok(`${person.name} removed.`),
                onError: (e) => toast.fail(e),
              });
            }}
          >
            <IconTrash size={15} />
          </button>
        )}
      </td>
    </tr>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const add = useAddPerson();
  const toast = useToast();

  return (
    <Modal
      title="Add a mobile number"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn primary"
            disabled={add.isPending || !name.trim() || mobile.replace(/\D/g, "").length < 10}
            onClick={() =>
              add.mutate(
                { name: name.trim(), mobile, isAdmin },
                {
                  onSuccess: () => {
                    toast.ok(`${name.trim()} can now sign in.`);
                    onClose();
                  },
                  onError: (e) => toast.fail(e),
                },
              )
            }
          >
            {add.isPending ? <span className="spinner" /> : "Add"}
          </button>
        </>
      }
    >
      <div className="col" style={{ gap: 13 }}>
        <div className="field">
          <label htmlFor="pname">Name</label>
          <input id="pname" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </div>
        <div className="field">
          <label htmlFor="pmobile">Mobile number</label>
          <input
            id="pmobile"
            type="tel"
            inputMode="numeric"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="10 digits"
            maxLength={14}
          />
          <span className="hint">
            They sign in with a code texted to this number. No password is ever set.
          </span>
        </div>
        <div className="switch-row">
          <div>
            <div className="t">Administrator</div>
            <div className="d">
              Can add and remove people. Nothing more — an admin cannot see or trade
              anybody else's Zerodha account.
            </div>
          </div>
          <button
            className={`switch ${isAdmin ? "on" : ""}`}
            onClick={() => setIsAdmin((a) => !a)}
            aria-pressed={isAdmin}
            aria-label="Administrator"
          />
        </div>
      </div>
    </Modal>
  );
}
